/* ============================================================
   somnair-ob.js — comportements de la famille « Orthopedic Bowl ».

   REGLE ABSOLUE : aucun prix n'est calcule ni formate ici. Tout montant affiche
   vient du Liquid (`money_without_trailing_zeros`), sinon la devise et les
   arrondis divergent du checkout. Ce fichier ne fait que de l'interface.

   Tout s'initialise sur des attributs `data-smob-*`, jamais sur des ids : les
   sections peuvent etre dupliquees dans le template sans collision.
   ============================================================ */
(function () {
  'use strict';

  function all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function pad(n) { return (n < 10 ? '0' : '') + n; }

  /* Prepare la copie d'une diapo pour un carrousel en boucle (galerie, stories).
     Les attributs de l'editeur de theme ne doivent exister qu'UNE fois :
     dupliques, `data-shopify-editor-block` fait pointer deux cadres de selection
     sur le meme block. Meme raison pour le focus : un clone ne doit pas ajouter
     d'etape au parcours clavier, il n'existe que pour l'oeil. */
  function declone(node) {
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('data-clone', '1');
    all('[data-shopify-editor-block]', node).forEach(function (el) {
      el.removeAttribute('data-shopify-editor-block');
    });
    node.removeAttribute('data-shopify-editor-block');
    all('button', node).forEach(function (b) { b.setAttribute('tabindex', '-1'); });
    return node;
  }

  /* ---------------------------------------------------------- galerie

     Le rail principal defile NATIVEMENT (`scroll-snap`, pose dans la section) :
     aucune ligne de JS ne se trouve dans le chemin du geste. Le doigt est donc
     suivi au pixel pres, l'inertie est celle du systeme, et le rendu tient la
     frequence de l'ecran parce que tout se passe sur le compositeur.

     Ce qu'il y avait avant, et pourquoi ca saccadait en mobile : UNE seule
     image dont on echangeait le `src`. Le glissement n'etait lu qu'au
     `touchend`, passe 42 px — rien ne bougeait pendant le geste, puis l'image
     changeait d'un coup, sans transition. Aucun reglage d'easing n'aurait
     rattrape ca : il n'y avait rien a animer.

     Il ne reste donc ici que trois roles : LIRE l'index pendant le defilement,
     DEPLACER le rail quand on clique une vignette ou une fleche, et REMBOBINER
     silencieusement aux extremites (voir plus bas).

     BOUCLE SANS FIN — meme principe que initCarousel : une copie de la derniere
     diapo est posee AVANT la premiere, une copie de la premiere APRES la
     derniere. Le rail contient donc n+2 vues, et la vue d'ouverture est a
     l'index 1, pas 0. Depasser une extremite fait donc glisser vers un clone,
     qui montre exactement la meme image que la vraie diapo jumelle : une fois
     le defilement arrete, on replace `scrollLeft` sur la jumelle d'un coup sec.
     Rien ne bouge a l'ecran (meme pixel), et le rail se retrouve avec de la
     matiere des deux cotes — donc on peut continuer indefiniment.

     Pourquoi des clones et pas un simple `scrollLeft = 0` en fin de rail : ici
     le geste appartient au navigateur (defilement natif). Sans clone, le doigt
     bute sur le bord — il n'y a rien a faire defiler — et la boucle ne pourrait
     etre que le saut sec des fleches qu'on avait, jamais un glissement.

     Le rembobinage attend l'ARRET du defilement (140 ms sans evenement) et ne
     se declenche jamais pendant un contact : deplacer le rail sous un doigt
     encore pose, ou en pleine inertie iOS, se verrait. */

  function initGallery(root) {
    var rail = root.querySelector('[data-smob-rail]');
    if (!rail) return;
    /* Une seule initialisation par rail : rejouee, elle reclonerait les
       extremites — et `reals` compterait alors les clones comme des diapos. */
    if (rail.hasAttribute('data-smob-ready')) return;
    rail.setAttribute('data-smob-ready', '1');
    var reals = all('[data-smob-slide]', rail);
    var thumbs = all('[data-smob-thumb]', root);
    var n = reals.length;
    if (!n) return;

    var raf = 0, lockUntil = 0, idleTimer = 0, touching = false;
    var calm = window.matchMedia('(prefers-reduced-motion: reduce)');
    function mode() { return calm.matches ? 'auto' : 'smooth'; }

    /* `off` = decalage entre l'index REEL (celui des vignettes) et l'index DOM
       (celui des positions du rail). 1 des qu'il y a un clone de tete. */
    var loop = n > 1;
    var slides = reals, off = 0;
    if (loop) {
      rail.appendChild(declone(reals[0].cloneNode(true)));
      rail.insertBefore(declone(reals[n - 1].cloneNode(true)), reals[0]);
      slides = all('[data-smob-slide]', rail);
      off = 1;
    }

    var i = 0;                 /* index REEL affiche (0..n-1), pour les vignettes */
    var pos = off;             /* index DOM courant (0..n+1)                      */

    /* Le pas se mesure entre deux diapos plutot que sur `clientWidth` : une
       gouttiere ou une bordure ajoutee plus tard est comptee sans y penser. */
    function step() {
      if (slides.length < 2) { return rail.clientWidth || 1; }
      return (slides[1].offsetLeft - slides[0].offsetLeft) || rail.clientWidth || 1;
    }

    /* Poser le clone de tete decale tout le rail d'une vue : on se recale AVANT
       que le navigateur ne peigne (meme tache JS), sinon la page s'ouvrirait sur
       la derniere image. */
    if (loop) rail.scrollLeft = Math.round(off * step());

    /* Video de la galerie : lue — donc telechargee — seulement quand sa diapo
       est reellement a l'ecran (19/10). Mesure : avec `autoplay`, les 2,3 Mo du
       MP4 1080p partaient des le chargement (691 -> 1 207 ms), malgre
       preload="metadata", meme si le client ne balayait jamais jusqu'a elle.
       root null = la fenetre, apres decoupe par le rail : la diapo doit etre a
       60 % visible, horizontalement ET verticalement. */
    if ('IntersectionObserver' in window) {
      var vio = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var v = en.target;
          if (en.isIntersecting) {
            if (v.preload !== 'auto') v.preload = 'auto';
            var pr = v.play();
            if (pr && pr.catch) pr.catch(function () {});
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { threshold: 0.6 });
      all('video', rail).forEach(function (v) { vio.observe(v); });
    }

    function real(p) { return ((p - off) % n + n) % n; }

    function scrollTo(el, x, behavior) {
      x = Math.max(0, Math.min(x, el.scrollWidth - el.clientWidth));
      if (el.scrollTo) { el.scrollTo({ left: x, behavior: behavior }); }
      else { el.scrollLeft = x; }
    }

    function paint(k) {
      if (k === i) return;
      i = k;
      thumbs.forEach(function (t, j) { t.classList.toggle('is-active', j === i); });
      var t = thumbs[i], row = t && t.parentNode;
      if (!row) return;
      scrollTo(row, t.offsetLeft - row.clientWidth / 2 + t.offsetWidth / 2, mode());
    }

    /* Un clone et sa jumelle affichent le meme fichier, mais deux lectures
       independantes : sans recopier l'instant courant, le rembobinage se verrait
       comme un saut dans la video. (Aujourd'hui la demo est au milieu du
       carrousel, donc jamais clonee — c'est pour le jour ou on la deplacera en
       tete ou en queue depuis l'editeur.) */
    function syncVideo(from, to) {
      var a = from && from.querySelector('video'), b = to && to.querySelector('video');
      if (!a || !b) return;
      try { b.currentTime = a.currentTime; } catch (e) {}
      if (b.paused) { var pr = b.play(); if (pr && pr.catch) pr.catch(function () {}); }
    }

    /* Rembobinage : on n'est sur un clone que si `pos` vaut 0 ou n+1. Le saut est
       SEC — meme image a l'ecran avant et apres, donc invisible — et `paint`
       n'est pas rappele : l'index reel, lui, n'a pas change. */
    function normalize() {
      if (!loop || (pos !== 0 && pos !== n + 1)) return false;
      var to = pos === 0 ? n : 1;
      syncVideo(slides[pos], slides[to]);
      pos = to;
      lockUntil = Date.now() + 80;
      scrollTo(rail, Math.round(to * step()), 'auto');
      return true;
    }

    /* 140 ms sans le moindre evenement de defilement = le rail est pose (snap
       compris). C'est le seul moment ou l'on peut deplacer `scrollLeft` sans que
       ca se voie. On relit la position plutot que de faire confiance a `pos` :
       apres un glissement au doigt, c'est le rail qui a raison. */
    function onIdle() {
      if (touching) return;
      pos = Math.max(0, Math.min(slides.length - 1, Math.round(rail.scrollLeft / step())));
      paint(real(pos));
      normalize();
    }
    function idle() {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(onIdle, 140);
    }

    /* `scroll` part a chaque image du geste : on ne calcule qu'une fois par
       frame, et `paint` ne touche a rien tant que l'index n'a pas change. */
    rail.addEventListener('scroll', function () {
      idle();
      if (raf || Date.now() < lockUntil) return;
      raf = window.requestAnimationFrame(function () {
        raf = 0;
        pos = Math.max(0, Math.min(slides.length - 1, Math.round(rail.scrollLeft / step())));
        paint(real(pos));
      });
    }, { passive: true });

    /* Pendant un defilement programme, les positions intermediaires ne sont pas
       des choix de l'utilisateur : les relayer ferait clignoter la vignette
       active sur toutes celles traversees. On les ignore le temps du trajet —
       mais le moindre contact reprend la main, le navigateur annulant de son
       cote le defilement en cours. */
    rail.addEventListener('pointerdown', function () { lockUntil = 0; }, { passive: true });
    rail.addEventListener('touchstart', function () { lockUntil = 0; }, { passive: true });

    /* `p` est un index DOM (clones compris) : `goTo(0)` va sur le clone de queue,
       pas sur la premiere vue. Le trajet reste `smooth` meme pour un passage
       d'extremite — c'est tout l'interet des clones : la vue suivante est
       vraiment la, on glisse dessus, et le rembobinage arrive apres coup. */
    function goTo(p, instant) {
      instant = instant || calm.matches;
      pos = p;
      lockUntil = Date.now() + (instant ? 60 : 700);
      scrollTo(rail, Math.round(p * step()), instant ? 'auto' : 'smooth');
      paint(real(p));
    }

    root.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-smob-thumb]');
      if (!t) return;
      var j = thumbs.indexOf(t);
      if (j > -1) goTo(j + off, false);
    });

    all('[data-smob-gnav]', root).forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var d = b.getAttribute('data-smob-gnav') === 'next' ? 1 : -1;
        if (!loop) { goTo(Math.max(0, Math.min(n - 1, pos + d)), false); return; }
        /* Deux clics coup sur coup au bord : le premier laisse le rail sur un
           clone, et la cible suivante sortirait du rail (elle serait bornee par
           `scrollTo`, donc le carrousel s'arreterait la). On recale d'abord. */
        if (pos + d > n + 1 || pos + d < 0) normalize();
        goTo(pos + d, false);
      });
    });

    /* Les vues suivantes sont `loading=lazy` pour ne pas disputer la bande
       passante a la LCP. Une fois la page chargee on les libere : un flick
       rapide ne doit pas traverser une case blanche. */
    function warm() {
      slides.forEach(function (s) {
        var im = s.querySelector('img');
        if (im && im.loading === 'lazy') { im.loading = 'eager'; }
      });
    }
    if (document.readyState === 'complete') { warm(); }
    else { window.addEventListener('load', warm, { once: true }); }

    /* Rotation de l'ecran : on se recale sur la vue courante. Jamais pendant un
       contact — en mobile, la barre d'adresse qui se replie emet un `resize` en
       plein milieu d'un glissement.
       Le lever du doigt rearme le rembobinage : un geste qui finit pile sur un
       clone laisse `onIdle` passer son tour (il refuse de bouger le rail sous un
       doigt pose), il faut donc lui redonner sa chance juste apres. */
    function release() { touching = false; idle(); }
    rail.addEventListener('touchstart', function () { touching = true; }, { passive: true });
    rail.addEventListener('touchend', release, { passive: true });
    rail.addEventListener('touchcancel', release, { passive: true });
    window.addEventListener('resize', function () {
      if (touching) return;
      lockUntil = Date.now() + 60;
      scrollTo(rail, Math.round(pos * step()), 'auto');
    });
  }

  /* -------------------------------------------------------- countdown

     Trois modes, repris de somnair-announce.liquid :
       deadline  — echeance reelle, la meme que le `endsAt` des remises
                   automatiques. C'est le mode par defaut, et le seul honnete
                   quand on annonce « l'offre se termine bientot ».
       daily     — remise a zero a 23:59, heure du visiteur.
       duration  — N heures glissantes, memorisees par session.
     Une echeance vide ou depassee retombe sur `daily` plutot que d'afficher un
     00:00:00 fige — un compteur mort abime plus la page qu'il ne la sert. */

  function initCountdown(el) {
    var mode = el.getAttribute('data-mode') || 'deadline';
    var hideDone = el.getAttribute('data-hide-done') === 'true';
    var box = el.closest('[data-smob-countdown-box]') || el;
    var deadline = null;

    if (mode === 'deadline') {
      var raw = (el.getAttribute('data-deadline') || '').trim();
      var t = raw ? Date.parse(raw.replace(' ', 'T')) : NaN;
      if (!isNaN(t) && t > Date.now()) deadline = t;
      else mode = 'daily';
    }
    /* AUDIT 03/08 — la reference est en `type:"duration", minutes:14` : un compteur
       de session en mm:ss, pas une echeance a plusieurs jours. `data-minutes` sert
       ce cas ; `data-hours` reste pour les autres pages de la famille.
       Comme chez eux le compteur repart au bout des 14 min (sinon il afficherait
       00:00 pour tout le reste de la session). C'est le prix de la parite 1:1 :
       repasser en `data-mode="deadline"` suffit a retrouver une echeance vraie. */
    var durMs = 0;
    if (mode === 'duration') {
      var mins = parseFloat(el.getAttribute('data-minutes') || '0');
      var hours = parseFloat(el.getAttribute('data-hours') || '0');
      durMs = mins ? mins * 60000 : (hours || 10) * 3600000;
      var key = 'smobT0';
      var t0 = parseInt(sessionStorage.getItem(key) || '0', 10);
      if (!t0) { t0 = Date.now(); try { sessionStorage.setItem(key, String(t0)); } catch (e) {} }
      deadline = t0 + durMs;
    }

    function target() {
      if (mode === 'daily') {
        /* Fin de journee du VISITEUR : 23:59:00 dans SON fuseau — `new Date()` et
           setHours() travaillent en heure locale du navigateur, jamais en UTC ni
           en heure boutique. Le compteur atteint donc 00:00:00 pile a 23h59.
           Passe 23:59, on bascule sur le 23:59 du lendemain : sinon la derniere
           minute de la journee afficherait un 00:00:00 fige (mode `daily` n'est
           jamais masque par `hide-done`). */
        var d = new Date(); d.setHours(23, 59, 0, 0);
        if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1);
        return d.getTime();
      }
      return deadline;
    }

    var slots = {
      d: el.querySelector('[data-d]'), h: el.querySelector('[data-h]'),
      m: el.querySelector('[data-m]'), s: el.querySelector('[data-s]'),
      /* Slot unique « mm:ss » — le format de la reference. Quand il est present,
         les quatre champs d/h/m/s ne sont pas rendus du tout. */
      mmss: el.querySelector('[data-mmss]')
    };

    function tick() {
      var left = Math.max(0, target() - Date.now());
      if (left <= 0 && mode === 'duration' && durMs) {
        /* Relance de session, comme chez eux. */
        var t1 = Date.now();
        try { sessionStorage.setItem('smobT0', String(t1)); } catch (e) {}
        deadline = t1 + durMs;
        left = durMs;
      }
      if (left <= 0 && mode !== 'daily') {
        if (hideDone) box.setAttribute('hidden', '');
        return;
      }
      var total = Math.floor(left / 1000);
      var d = Math.floor(total / 86400);
      var h = Math.floor(total % 86400 / 3600);
      var m = Math.floor(total % 3600 / 60);
      var s = total % 60;
      if (slots.mmss) {
        /* mm:ss, en cumulant les heures/jours eventuels dans les minutes — la
           reference ne descend jamais sous 14 min, donc on reste a 2 chiffres. */
        slots.mmss.textContent = pad(Math.floor(total / 60)) + ':' + pad(s);
        el.setAttribute('data-left', total);
        return;
      }
      /* Les jours n'apparaissent que s'il y en a : « 02:14:07 » se lit mieux
         que « 00j 02:14:07 » sur une offre du jour. */
      if (slots.d) {
        slots.d.textContent = d ? d + 'd ' : '';
        slots.d.hidden = !d;
      }
      if (slots.h) slots.h.textContent = pad(h);
      if (slots.m) slots.m.textContent = pad(m);
      if (slots.s) slots.s.textContent = pad(s);
      el.setAttribute('data-left', total);
    }
    tick();
    setInterval(tick, 1000);
    box.removeAttribute('hidden');
  }

  /* ---------------------------------------------- date de livraison */

  function initShipDate(el) {
    var from = parseInt(el.getAttribute('data-from') || '5', 10);
    var to = parseInt(el.getAttribute('data-to') || '10', 10);
    var locale = el.getAttribute('data-locale') || 'en-US';

    /* AUDIT 03/08 — leur chaine est figee dans le Liquid : « Aug 8 – Aug 13 », soit
       J+5 / J+10 en jours CALENDAIRES, mois repete des deux cotes. On calculait en
       jours OUVRES et on abregeait la borne haute : sur la meme date de capture on
       affichait « Aug 10 – 17 », deux et quatre jours plus tard qu'eux. */
    function calendarDays(n) {
      var d = new Date();
      d.setDate(d.getDate() + n);
      return d;
    }
    function fmt(d) { return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' }); }

    el.textContent = fmt(calendarDays(from)) + ' – ' + fmt(calendarDays(to));
  }

  /* ------------------------------------------------- mini-avis rotatif */

  function initQuotes(root) {
    var data;
    var holder = root.querySelector('[data-smob-quotes]');
    if (!holder) return;
    try { data = JSON.parse(holder.textContent); } catch (e) { return; }
    if (!data || data.length < 2) return;

    var nm = root.querySelector('[data-smob-qname]');
    var tx = root.querySelector('[data-smob-qtext]');
    var av = root.querySelector('[data-smob-qav]');
    var k = 0, timer;

    function paint() {
      var q = data[k];
      if (nm) nm.textContent = q.n;
      if (av) {
        if (q.i) av.innerHTML = '<img src="' + q.i + '" alt="" loading="lazy">';
        else av.textContent = (q.n || '?').charAt(0);
      }
      if (!tx) return;
      tx.style.opacity = 0;
      setTimeout(function () { tx.textContent = '“' + q.t + '”'; tx.style.opacity = 1; }, 180);
    }
    function go(step) {
      k = (k + step + data.length) % data.length;
      paint();
      clearInterval(timer);
      timer = setInterval(function () { go(1); }, 6000);
    }
    all('[data-smob-qnav]', root).forEach(function (b) {
      b.addEventListener('click', function () { go(parseInt(b.getAttribute('data-smob-qnav'), 10)); });
    });
    paint();
    timer = setInterval(function () { go(1); }, 6000);
  }

  /* ------------------------------------------------- paliers de quantite */

  function initTiers(root) {
    var cards = all('[data-smob-tier]', root);
    if (!cards.length) return;
    /* Chaque palier est une VARIANTE : selectionner une carte change l'id envoye
       a /cart/add, pas la quantite. C'est la variante qui porte le prix du pack.

       On remonte au FORMULAIRE pour trouver l'input : il est un frere du bloc de
       paliers, pas un descendant. Un `root.querySelector` ne le trouvait pas, et
       l'echec etait SILENCIEUX — la carte s'allumait normalement mais le panier
       recevait toujours la variante par defaut. Detecte en sondant le panier :
       pack de 4 selectionne, 3999 c factures au lieu de 6199. */
    var form = root.closest('form') || root.closest('[data-smob-form]');
    var variantInput = (form || document).querySelector('[data-smob-vid]');
    var qtyInput = (form || document).querySelector('[data-smob-qtyinput]');

    function select(card) {
      cards.forEach(function (c) {
        var on = c === card;
        c.setAttribute('aria-checked', on ? 'true' : 'false');
        var r = c.querySelector('input[type=radio]');
        if (r) r.checked = on;
        /* `is-active` va sur le CONTENEUR, pas sur le rang cliquable : ce sont les
           barres de cadeaux, situees en dehors du <label>, qui doivent aussi
           passer en bleu quand le palier est choisi. */
        var wrap = c.closest('[data-smob-tierwrap]') || c;
        wrap.classList.toggle('is-active', on);
      });
      /* MODE QUANTITE (le chew) — la carte porte `data-smob-qty` et c'est la
         QUANTITE qu'elle ecrit ; la variante, elle, vient du selecteur de
         taille. Sans cet attribut on garde le comportement d'origine, celui des
         trois pages gamelle : la carte ecrit la variante et la quantite reste
         a 1. Les deux axes ne se marchent jamais dessus. */
      var qty = card.getAttribute('data-smob-qty');
      if (qty) {
        if (qtyInput) qtyInput.value = qty;
        return;
      }
      var vid = card.getAttribute('data-smob-tier');
      if (variantInput && vid) variantInput.value = vid;
    }
    cards.forEach(function (c) {
      c.addEventListener('click', function () { select(c); });
      c.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); select(c); }
      });
    });
    select(root.querySelector('[data-smob-tier][data-default]') || cards[0]);
  }

  /* --------------------------------------------- selecteur de taille (chew)

     N'existe qu'en mode « quantite » : le produit a plusieurs variantes, et
     changer de taille change TOUS les prix des cartes de palier.

     Le JS ne CALCULE aucun prix — il echange lequel des jeux pre-rendus par le
     Liquid est visible. C'est la regle de la maison : ce qui s'affiche est ce
     que le Liquid a calcule, jamais un arrondi de JavaScript. */

  /* Deux axes decident quel montant est visible : la VARIANTE choisie et le
     MODE d'achat (one-time / abonnement). Les deux selecteurs peignent par le
     meme chemin, sinon changer de taille en mode abonne reafficherait les prix
     one-time — c'est exactement le genre d'ecart qui fait payer autre chose que
     ce qui etait affiche.

     Un montant sans `data-smob-md` (les pages gamelle, qui n'ont pas
     d'abonnement) n'est filtre que par la variante : leur rendu ne change pas
     d'un pixel. */

  function currentMode(scope) {
    var on = scope.querySelector('[data-smob-mode].is-active');
    return on ? on.getAttribute('data-smob-mode') : 'otp';
  }

  function paintAmounts(scope, vid) {
    var md = currentMode(scope);
    all('[data-smob-pv]', scope).forEach(function (el) {
      var m = el.getAttribute('data-smob-md');
      el.hidden = el.getAttribute('data-smob-pv') !== vid || (m !== null && m !== md);
    });
    /* MODE VARIANTE (une carte = une variante). Ces montants-la ne peuvent pas
       etre filtres par la variante courante, sinon les cartes non selectionnees
       perdraient leur prix. Ils ne dependent que du mode d'achat. */
    all('[data-smob-tmd]', scope).forEach(function (el) {
      el.hidden = el.getAttribute('data-smob-tmd') !== md;
    });
  }

  /* ------------------------------------------ onglets one-time / abonnement

     Le JS ne calcule aucun prix ici non plus : il montre l'un des deux jeux
     rendus par le Liquid, et il ACTIVE ou DESACTIVE l'input `selling_plan`.
     Desactive, l'input n'est pas envoye : le corps de /cart/add redevient
     exactement celui d'avant l'abonnement. */

  function initModes(root) {
    var opts = all('[data-smob-mode]', root);
    if (!opts.length) return;
    var form = root.closest('form') || root.closest('[data-smob-form]');
    var scope = form || document;
    var planInput = scope.querySelector('[data-smob-sp]');
    var variantInput = scope.querySelector('[data-smob-vid]');
    var terms = scope.querySelector('[data-smob-subterms]');
    /* Presentation `checkbox` : les onglets sont masques mais toujours la, et
       c'est eux qui portent l'etat. La case n'est qu'un second declencheur du
       meme `select()` — aucun etat n'est duplique. */
    var subCheck = scope.querySelector('[data-smob-subcheck]');

    function planForCurrentSize() {
      var sizeBtn = scope.querySelector('[data-smob-size].is-active');
      if (sizeBtn) return sizeBtn.getAttribute('data-smob-plan') || '';
      /* Produit a variante unique : pas de selecteur de taille, le plan est
         celui pose par le Liquid a l'ouverture. */
      return planInput ? planInput.getAttribute('value') || '' : '';
    }

    function select(md) {
      /* Une taille sans plan ne peut pas etre abonnee : on retombe en one-time
         plutot que d'envoyer un `selling_plan` vide, que Shopify refuserait. */
      if (md === 'sub' && !planForCurrentSize()) md = 'otp';
      opts.forEach(function (o) {
        var on = o.getAttribute('data-smob-mode') === md;
        o.classList.toggle('is-active', on);
        o.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      if (planInput) {
        if (md === 'sub') {
          planInput.value = planForCurrentSize();
          planInput.disabled = !planInput.value;
        } else {
          planInput.disabled = true;
        }
      }
      if (terms) terms.hidden = md !== 'sub';
      /* Repli one-time sur une taille sans plan : la case doit se DECOCHER,
         sinon elle promettrait un abonnement que le formulaire n'envoie pas. */
      if (subCheck) subCheck.checked = md === 'sub';
      paintAmounts(scope, variantInput ? variantInput.value : '');
    }

    opts.forEach(function (o) {
      o.addEventListener('click', function () { select(o.getAttribute('data-smob-mode')); });
    });
    if (subCheck) {
      subCheck.addEventListener('change', function () {
        select(subCheck.checked ? 'sub' : 'otp');
      });
    }
    /* `scope` est le formulaire : c'est lui que le selecteur de taille previent
       quand la taille change, pour que le plan suive la variante. */
    scope.addEventListener('smob:size', function () { select(currentMode(scope)); });
    select(currentMode(scope));
  }

  function initSizes(root) {
    var opts = all('[data-smob-size]', root);
    if (!opts.length) return;
    var form = root.closest('form') || root.closest('[data-smob-form]');
    var scope = form || document;
    var variantInput = scope.querySelector('[data-smob-vid]');

    function select(btn) {
      var vid = btn.getAttribute('data-smob-size');
      if (!vid) return;
      opts.forEach(function (o) {
        var on = o === btn;
        o.classList.toggle('is-active', on);
        o.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      if (variantInput) variantInput.value = vid;
      /* Un jeu de montants par variante, sur les cartes ET sur les pastilles
         d'economie. On les prend dans le FORMULAIRE : les prendre dans `root`
         ne ramasserait que le selecteur lui-meme. */
      paintAmounts(scope, vid);
      /* Le plan d'abonnement est porte par la taille : prevenir les onglets,
         qui reecrivent `selling_plan` et repeignent. */
      try { scope.dispatchEvent(new CustomEvent('smob:size', { detail: { id: vid } })); } catch (e) {}
    }

    opts.forEach(function (o) {
      o.addEventListener('click', function () { if (!o.disabled) select(o); });
    });
    var start = opts.filter(function (o) { return o.classList.contains('is-active'); })[0];
    select(start || opts.filter(function (o) { return !o.disabled; })[0] || opts[0]);
  }

  /* ----------------------------------------------- barre de stock reelle */

  function initStock(el) {
    /* Le pourcentage est calcule en Liquid depuis l'inventaire Shopify et pose
       en --smob-pct. Le JS ne fait que reveler et animer : s'il ne tourne pas,
       la page n'affiche pas une barre a un chiffre invente. */
    if (!el.getAttribute('data-pct')) return;
    el.removeAttribute('hidden');
    var fill = el.querySelector('[data-smob-fill]');
    if (!fill || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        fill.style.width = 'var(--smob-pct)';
        io.disconnect();
      });
    }, { threshold: .35 });
    io.observe(el);
  }

  /* ------------------------------------------------------- carrousel UGC */

  function initCarousel(root) {
    var track = root.querySelector('[data-smob-track]');
    var dotsBox = root.querySelector('[data-smob-dots]');
    if (!track) return;
    var reals = all('[data-smob-slide]', track);
    var n = reals.length;
    if (n < 2) return;

    /* Clones de tete et de queue : la boucle ne « rembobine » jamais visiblement. */
    var first = reals[0].cloneNode(true), last = reals[n - 1].cloneNode(true);
    first.setAttribute('data-clone', '1');
    last.setAttribute('data-clone', '1');
    track.appendChild(first);
    track.insertBefore(last, reals[0]);

    var k = 0, animating = false;

    function width() {
      var s = track.querySelector('[data-smob-slide]');
      if (!s) return 0;
      var gap = parseFloat(getComputedStyle(track).gap) || 0;
      return s.getBoundingClientRect().width + gap;
    }
    function place(anim) {
      track.style.transition = anim ? '' : 'none';
      track.style.transform = 'translateX(' + (-(k + 1) * width()) + 'px)';
      if (!anim) track.offsetHeight;                       // force le reflow
    }
    function dots() {
      if (!dotsBox) return;
      dotsBox.innerHTML = '';
      for (var j = 0; j < n; j++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'smob-dot' + (j === k ? ' on' : '');
        b.setAttribute('aria-label', 'Slide ' + (j + 1));
        (function (idx) { b.addEventListener('click', function () { go(idx - k); }); })(j);
        dotsBox.appendChild(b);
      }
    }
    function go(step) {
      if (animating) return;
      animating = true;
      k += step;
      place(true);
      setTimeout(function () {
        if (k >= n) { k = 0; place(false); }
        else if (k < 0) { k = n - 1; place(false); }
        animating = false;
        dots();
      }, 560);
    }

    all('[data-smob-cnav]', root).forEach(function (b) {
      b.addEventListener('click', function () { go(parseInt(b.getAttribute('data-smob-cnav'), 10)); });
    });

    var sx = 0, sy = 0, swiping = false;
    track.addEventListener('touchstart', function (e) {
      sx = e.touches[0].clientX; sy = e.touches[0].clientY; swiping = true;
    }, { passive: true });
    track.addEventListener('touchend', function (e) {
      if (!swiping) return;
      swiping = false;
      var dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy;
      if (Math.abs(dx) > 42 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
    }, { passive: true });

    place(false);
    dots();
    window.addEventListener('resize', function () { place(false); });
  }

  /* ------------------------------------------- carrousel de stories Instagram

     Section `somnair-ob-story`. Ce n'est PAS initCarousel avec un minuteur en
     plus : trois differences de fond, et chacune est la raison d'une fonction
     separee plutot que d'une option.

     1. LA BARRE EST LE MINUTEUR. Le passage a la story suivante est declenche
        par l'`animationend` du segment en cours. Un setInterval en parallele
        derive de quelques dizaines de ms par tour et finit par annoncer un
        passage qui n'a pas lieu. Corollaire : mettre en pause, c'est une seule
        propriete CSS (`animation-play-state`), pas un minuteur a reconstruire.

     2. TROIS JEUX DE DIAPOS, pas un clone de chaque cote. initCarousel montre
        une diapo a la fois ; ici les voisines depassent, et en ordinateur on en
        voit jusqu'a cinq. Un seul clone laisserait un trou au bord pendant le
        rembobinage.

     3. Le segment qui se remplit est arme sur LES TROIS copies du meme index
        (`is-live`), pas sur la seule diapo visible. Sinon la barre repartirait
        de zero au moment ou l'on recentre silencieusement la piste, en plein
        milieu de la transition.

     Le reste est du confort : glisser doigt ET souris, zones de tap facon
     Instagram, pause au contact, hors ecran et onglet masque. */

  function initStories(root) {
    var track = root.querySelector('[data-smob-st-track]');
    if (!track) return;
    var reals = all('[data-smob-st-slide]', track);
    var n = reals.length;
    if (!n) return;

    var view = track.parentNode;
    var dotsBox = root.querySelector('[data-smob-st-dots]');
    var looping = n > 1;
    var slides = reals;
    var k = 0;          /* index logique de la story courante, 0..n-1 */
    var r = 0;          /* index RENDU dans la piste triplee            */
    var animating = false, settle = 0;
    var holds = {};

    /* `declone` est en tete de fichier : la galerie s'en sert aussi depuis que
       son rail boucle. */

    if (looping) {
      var before = document.createDocumentFragment();
      var after = document.createDocumentFragment();
      reals.forEach(function (s) {
        before.appendChild(declone(s.cloneNode(true)));
        after.appendChild(declone(s.cloneNode(true)));
      });
      track.insertBefore(before, reals[0]);
      track.appendChild(after);
      slides = all('[data-smob-st-slide]', track);
      r = n;
    }

    /* Etat des segments : fige une fois pour toutes. La diapo d'index j montre
       ses j premiers segments pleins et arme le sien ; l'animation, elle, ne
       part que quand la diapo recoit `is-live`. Rien a recalculer a chaque pas. */
    slides.forEach(function (s, i) {
      var j = i % n;
      all('.smob-st__seg', s).forEach(function (seg, m) {
        seg.classList.toggle('is-done', m < j);
        seg.classList.toggle('is-run', m === j);
      });
    });

    function metrics() {
      var gap = parseFloat(getComputedStyle(track).gap) || 0;
      var sw = slides[0].getBoundingClientRect().width;
      return { sw: sw, step: sw + gap };
    }

    /* La diapo courante est CENTREE dans la fenetre, pas calee a gauche : c'est
       ce qui fait depasser les voisines des deux cotes, en mobile comme en
       ordinateur, sans un chiffre de decalage par point de rupture. */
    function place(anim, extra) {
      var m = metrics();
      var x = (view.clientWidth / 2) - (m.sw / 2) - (r * m.step) + (extra || 0);
      track.style.transition = anim ? '' : 'none';
      track.style.transform = 'translate3d(' + x + 'px,0,0)';
      if (!anim) { void track.offsetHeight; }
    }

    function paint() {
      slides.forEach(function (s, i) {
        s.classList.toggle('is-on', i === r);
        s.classList.toggle('is-live', (i % n) === k);
      });
      if (dotsBox) {
        all('.smob-dot', dotsBox).forEach(function (d, j) { d.classList.toggle('on', j === k); });
      }
    }

    /* Cloture immediatement la transition en cours : recentrage silencieux
       compris. Sans elle, un `if (animating) return` avalait tout geste tombant
       dans les 520 ms d'une transition — releve en verification : une puce
       cliquee juste apres un passage automatique ne faisait rien. Instagram
       enchaine les taps, il ne les jette pas. */
    function settleNow() {
      if (!animating) return;
      window.clearTimeout(settle);
      animating = false;
      if (r !== n + k) { r = n + k; place(false); paint(); }
    }

    function go(d) {
      if (!d || !looping) return;
      settleNow();
      animating = true;
      r += d;
      k = (((k + d) % n) + n) % n;
      paint();
      place(true);
      settle = window.setTimeout(function () {
        animating = false;
        if (r !== n + k) { r = n + k; place(false); paint(); }
      }, 520);
    }

    /* Toujours par le plus court chemin : cliquer la 15e puce depuis la 1re doit
       reculer d'une, pas defiler quatorze fois. */
    function jumpTo(j) {
      var d = (((j - k) % n) + n) % n;
      if (d > n / 2) { d -= n; }
      go(d);
    }

    function setHold(key, on) {
      if (on) { holds[key] = 1; } else { delete holds[key]; }
      root.classList.toggle('is-hold', Object.keys(holds).length > 0);
    }

    /* ------------------------------------------------------- glisser */

    var dragging = false, axis = 0, sx = 0, sy = 0, dx = 0, suppress = false;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true; axis = 0; dx = 0;
      sx = e.clientX; sy = e.clientY;
      setHold('touch', true);          /* au contact, Instagram gele la barre */
    });

    function onMove(e) {
      if (!dragging) return;
      var mx = e.clientX - sx, my = e.clientY - sy;
      if (!axis) {
        if (Math.abs(mx) < 6 && Math.abs(my) < 6) return;
        /* Verdict d'axe une seule fois : au-dela, un geste vertical ne doit
           plus pouvoir capturer la piste, sinon la page ne defile plus. */
        axis = Math.abs(mx) > Math.abs(my) ? 1 : 2;
        if (axis === 1) { track.classList.add('is-drag'); }
      }
      if (axis !== 1) return;
      var m = metrics();
      var lim = m.step * 1.15;
      dx = Math.max(-lim, Math.min(lim, mx));
      place(false, dx);
    }

    function onUp() {
      if (!dragging) return;
      dragging = false;
      setHold('touch', false);
      track.classList.remove('is-drag');
      if (axis !== 1) { axis = 0; return; }
      var m = metrics();
      var moved = dx;
      dx = 0;
      suppress = Math.abs(moved) > 8;
      if (Math.abs(moved) > Math.min(42, m.sw * .18)) { go(moved < 0 ? 1 : -1); }
      else { place(true); }
      axis = 0;
      /* Le `click` de fin de geste part juste apres : on le laisse passer une
         fois la pile videe, sinon un glisser vaudrait aussi un tap. */
      window.setTimeout(function () { suppress = false; }, 0);
    }

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    /* ------------------------------------------- taps, fleches, clavier */

    root.addEventListener('click', function (e) {
      var nav = e.target.closest && e.target.closest('[data-smob-snav]');
      if (!nav) return;
      if (suppress) { e.preventDefault(); return; }
      var sl = nav.closest('[data-smob-st-slide]');
      if (sl) {
        var idx = slides.indexOf(sl) % n;
        /* Taper une voisine, c'est y aller — pas avancer d'un cran. */
        if (idx !== k) { jumpTo(idx); return; }
      }
      go(parseInt(nav.getAttribute('data-smob-snav'), 10));
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { go(-1); }
      else if (e.key === 'ArrowRight') { go(1); }
      else { return; }
      e.preventDefault();
    });

    /* --------------------------------------------- le minuteur, c'est la barre */

    track.addEventListener('animationend', function (e) {
      if (e.animationName !== 'smob-st-fill') return;
      var sl = e.target.closest && e.target.closest('[data-smob-st-slide]');
      /* Les trois copies de l'index courant animent en meme temps : on ne
         retient que celle du jeu du milieu, sinon on avancerait de trois. */
      if (!sl || slides.indexOf(sl) !== (looping ? n + k : k)) return;
      go(1);
    });

    /* ------------------------------------------------ pauses automatiques */

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { setHold('offscreen', !en.isIntersecting); });
      }, { threshold: .35 }).observe(root);
    }
    document.addEventListener('visibilitychange', function () {
      setHold('tab', document.hidden);
    });

    /* ------------------------------------------------------------ demarrage */

    if (dotsBox) {
      dotsBox.innerHTML = '';
      for (var j = 0; j < n; j++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'smob-dot';
        b.setAttribute('aria-label', 'Story ' + (j + 1));
        (function (idx) {
          b.addEventListener('click', function () { jumpTo(idx); });
        })(j);
        dotsBox.appendChild(b);
      }
    }

    var rz = 0;
    window.addEventListener('resize', function () {
      window.clearTimeout(rz);
      rz = window.setTimeout(function () { place(false); }, 120);
    });
    /* Les captures sont en `lazy` : la largeur des diapos vient du flex et de
       l'aspect-ratio, jamais du chargement. Un recalage au `load` couvre quand
       meme l'apparition tardive d'une barre de defilement. */
    window.addEventListener('load', function () { place(false); });

    paint();
    place(false);
  }

  /* ------------------------------------------------------ topbar rotative */

  function initTicker(root) {
    var items = all('[data-smob-tick]', root);
    if (items.length < 2) return;
    var k = 0;
    setInterval(function () {
      items[k].classList.remove('on');
      k = (k + 1) % items.length;
      items[k].classList.add('on');
    }, parseInt(root.getAttribute('data-interval') || '3000', 10));
  }

  /* ------------------------------------------- panier : ouverture immediate */

  function drawer() { return document.querySelector('cart-drawer'); }

  function openDrawer(loading) {
    var cd = drawer();
    if (!cd) return null;
    cd.classList.remove('is-empty');
    cd.classList.add('active', 'animate');
    if (loading) cd.classList.add('smob-loading');
    document.body.classList.add('overflow-hidden');
    return cd;
  }

  /* Aligne les LIBELLES du tiroir sur ceux de la reference. Ce sont des chaines
     du theme partage : on ne touche pas au fichier, on les reecrit apres chaque
     remplacement du contenu. Appele depuis swapDrawer/refreshDrawer, donc a un
     moment deterministe — pas via un MutationObserver, qui se declencherait
     aussi sur les pages sommeil et casserait leur tiroir. */
  /* Rend une promesse tenue quand le pied est COMPLET (recapitulatif pose,
     barre d'objectif recalculee). swapDrawer s'en sert pour ne devoiler le
     tiroir qu'une fois — sans quoi le contenu arrive d'abord sans
     « Discounts / Subtotal », puis le pied grandit de ~126 px sous les yeux du
     client et le bandeau du haut change de texte au passage. */
  function skinDrawer() {
    var cd = drawer();
    if (!cd) return Promise.resolve();
    var h = cd.querySelector('.drawer__heading, .cart-drawer__heading, h2');
    if (h && h.textContent.trim() !== 'Your cart') h.textContent = 'Your cart';
    /* Panier vide : buildSummary sort AVANT d'avoir lu /cart.js, et le compteur
       de l'en-tete resterait sur l'ancien nombre apres suppression de la
       derniere ligne. Le DOM du tiroir suffit a trancher, sans requete. */
    if (!cd.querySelector('.cart-item')) setCartCount(0);
    return buildSummary(cd);
  }

  /* Pastille de l'en-tete (somnair-ob-header). Le Liquid en rend la valeur au
     chargement ; ici on la RESYNCHRONISE apres chaque mutation du panier —
     l'en-tete n'est pas re-rendu, lui, quand une ligne change. */
  function setCartCount(n) {
    all('[data-smob-cart-count]').forEach(function (el) {
      el.textContent = n;
      if (n > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  /* Libelle du bouton : « Checkout • $61.99 », leur `footer.buttonText`.
     Le notre disait « Check out » en majuscules et laissait tomber le total. */
  function skinCheckout(cd, cart) {
    var btn = cd.querySelector('button[name=checkout], .cart__checkout-button, [type=submit]');
    if (!btn) return;
    var label = 'Checkout • ' + money(cart.total_price, cart);
    if (btn.textContent.trim() !== label) btn.textContent = label;
  }

  /* Barre d'objectif — leur `tieredPromotionsBar` : UN palier « Free shipping »
     a 55 $ depuis le 30/08 (48 $ avant), « Add {reste} to unlock Free
     shipping! » puis « Free shipping
     unlocked! ». Le theme, lui, vise 80 $ : sur un panier a 61,99 $ il reclamait
     18,01 $ de plus alors que la livraison etait deja offerte. On recalcule le
     texte ET le remplissage sur le seuil de la reference, sans toucher au reglage
     du theme, qui reste celui des pages sommeil. */
  var GOAL = 5500;

  function cartState() {
    try {
      var el = document.getElementById('smob-cart-state');
      if (el) return JSON.parse(el.textContent);
    } catch (e) { /* on retombe sur les valeurs par defaut */ }
    return null;
  }

  /* `cart` optionnel : au chargement on prend l'etat rendu par le Liquid, apres
     une mutation on passe le panier frais. Aucun appel reseau ici — la barre
     restait donc a 80 $ quand Shopify repondait 429 au XHR. */
  function goalBar(cd, cart) {
    var txt = cd.querySelector('.cart-progress__text');
    var bar = cd.querySelector('.cart-progress__bar');
    if (!txt && !bar) return;
    var st = cartState();
    if (st && st.goal) GOAL = st.goal;
    /* Le seuil doit etre dans la DEVISE DU PANIER (09/08, ouverture UK + AU).
       `total` vient de /cart.js ou du Liquid : dans les deux cas c'est la devise
       de presentation du marche. Comparer des pence a 5500 « cents US » faisait
       reclamer 55 £ la ou Shopify n'exige que 45 £. La table est rendue par
       somnair-ob-scope.liquid ; a defaut on retombe sur l'ancien seuil. */
    var cur = (cart && cart.currency) || (st && st.currency);
    if (st && st.goals && cur && st.goals[cur]) GOAL = st.goals[cur];
    var total = cart ? cart.total_price : (st ? st.total : 0);
    var left = Math.max(0, GOAL - total);
    if (txt) {
      txt.textContent = left > 0
        ? 'Add ' + money(left, cart) + ' to unlock Free shipping!'
        : 'Free shipping unlocked!';
    }
    var fill = bar && bar.firstElementChild;
    if (fill) fill.style.width = Math.min(100, Math.round(total / GOAL * 100)) + '%';
  }

  /* Recapitulatif de bas de panier : economie, livraison, total, moyens de
     paiement — plus le rappel du cadeau « FREE Priority Shipping » du pack de 4.

     TOUT vient de /cart.js, jamais d'une valeur ecrite ici :
       · l'economie = total barre - total paye, et la ligne DISPARAIT s'il n'y a
         rien a economiser (un « You save $0 » est pire que rien) ;
       · la livraison n'affiche « FREE » que si le panier franchit reellement le
         seuil de la boutique. En dessous, on ecrit « Calculated at checkout »
         plutot que de promettre une gratuite que le checkout dementira.
     Le seuil est lu sur la barre de progression du theme, qui le connait deja :
     quand elle annonce encore un reste a atteindre, c'est que ce n'est pas offert. */
  /* Vrai pendant que l'on ecrit dans le pied : l'observateur de mutations ne doit
     pas repartir sur nos propres insertions. */
  var skinning = false;

  /* ------------------------------------------ recomptage des cadeaux : ailleurs

     Il vivait ici. Depuis le 27/08 il vit dans somnair-cart.js, avec le reste
     du tiroir — parce que CE fichier-ci n'est charge que par somnair-ob-scope,
     donc sur la home et les pages gamelle, et PAS sur /cart, ni sur les pages
     legales, ni sur le blog. Or c'est exactement la que le client revoit son
     panier et clique sur Checkout : une ligne cadeau restee FACTUREE y passait
     sans aucun filet.

     Le contrat ne bouge pas : une promesse si une correction est partie (on
     rend la main, le repeint suivant reprendra l'habillage), null s'il n'y
     avait rien a corriger — le cas de tous les paniers sains. */
  function normaliseGifts(cart) {
    var g = window.somnairGifts;
    return (g && g.run(cart)) || null;
  }

  function buildSummary(cd) {
    var tpl = document.getElementById('smob-cart-summary');
    var footer = cd.querySelector('.drawer__footer, .cart-drawer__footer');
    if (!tpl || !footer) return Promise.resolve();

    skinning = true;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* Avant tout retour anticipe : c'est le seul point du fichier qui
           connaisse le nombre reel d'articles. */
        if (cart) setCartCount(cart.item_count || 0);
        if (!cart || !cart.items || !cart.items.length) { skinning = false; return; }

        /* Recomptage des lignes cadeau. Rend une promesse s'il a fallu ecrire
           dans le panier — auquel cas on rend la main, le repeint suivant
           reprendra l'habillage. */
        var corrige = normaliseGifts(cart);
        if (corrige) { skinning = false; return corrige; }

        ['.smob-cs', '.smob-cs__pay'].forEach(function (sel) {
          var o = footer.querySelector(sel);
          if (o) o.parentNode.removeChild(o);
        });
        var node = tpl.content.cloneNode(true);

        /* Prix barres transmis par le Liquid : /cart.js ne les contient pas.
           A defaut, on retombe sur le prix de ligne, et l'economie vaut zero —
           mieux vaut ne rien annoncer qu'annoncer un chiffre faux.

           DEUX sources, dans cet ordre — la seconde ecrase la premiere :
             · #smob-compare-at : les variantes de la PAGE. Rendu une seule fois
               au chargement, il ne voit donc PAS une ligne ajoutee depuis (le
               cross-sell du tiroir). Repli.
             · #smob-cart-ca : bloc custom_liquid DU TIROIR, re-rendu a chaque
               repaint, qui couvre exactement les lignes du panier. Autorite.
           Sans le second, un panier bowl B2G2 + feeder B1G1 annoncait
           « Discounts −$97.97 » au lieu de −$141.96 (constate le 08/08). */
        var compareAt = {};
        ['smob-compare-at', 'smob-cart-ca'].forEach(function (id) {
          try {
            var el = document.getElementById(id);
            if (!el) return;
            var map = JSON.parse(el.textContent);
            Object.keys(map).forEach(function (k) { compareAt[k] = map[k]; });
          } catch (e) { /* source indisponible : on garde ce qu'on a */ }
        });

        var full = 0;
        cart.items.forEach(function (it) {
          var ca = compareAt[String(it.variant_id)] || 0;
          full += ca > it.price ? ca * it.quantity : it.line_price;
        });
        var saved = Math.max(0, full - cart.total_price);
        if (saved > 0) {
          node.querySelector('[data-smob-cs-save]').removeAttribute('hidden');
          node.querySelector('[data-smob-cs-save-val]').textContent = '−' + money(saved, cart);
        }

        node.querySelector('[data-smob-cs-total]').textContent = money(cart.total_price, cart);

        /* ORDRE DU PIED, celui de leur `footer.modulesOrder` :
             upsellToggle -> discounts -> subtotal -> checkoutButton -> paymentIcons
           On avait tout empile en tete du pied, donc les icones de paiement
           passaient AU-DESSUS du bouton et l'assurance colis en dessous. */
        var rows = node.querySelector('.smob-cs');
        var pay = node.querySelector('.smob-cs__pay');
        var btn = footer.querySelector('.cart__checkout-button, button[name=checkout]');
        var ctas = footer.querySelector('.cart__ctas') || (btn ? btn.parentNode : null);
        if (ctas && ctas.parentNode !== footer) ctas = ctas.closest('.drawer__footer > *') || ctas;

        var prot = footer.querySelector('.smc-prot');
        if (prot && prot.parentNode === footer) footer.insertBefore(prot, footer.firstChild);

        if (rows) {
          if (ctas && ctas.parentNode === footer) footer.insertBefore(rows, ctas);
          else footer.insertBefore(rows, footer.firstChild);
        }
        if (pay) {
          if (ctas && ctas.parentNode === footer) footer.insertBefore(pay, ctas.nextSibling);
          else footer.appendChild(pay);
        }

        skinCheckout(cd, cart);
        goalBar(cd, cart);
        skinning = false;

        /* Plus de bandeau « + FREE Priority Shipping » en bas du tiroir : il
           n'existe pas chez eux, et il CONTREDISAIT la barre d'objectif juste
           au-dessus (« ajoutez X pour la livraison offerte » surmontant un
           « livraison offerte »). Leur equivalent est le suffixe « + free gift »
           sur le titre de la ligne, pose par `lineItems.withFreeGiftText`. */
        var stale = cd.querySelector('.smob-cg');
        if (stale) stale.parentNode.removeChild(stale);
      })
      .catch(function () {
        skinning = false; /* le tiroir reste utilisable sans le recapitulatif */
      });
  }

  /* ------------------------------------------------ resynchronisation du pied

     LE BUG (signale le 03/08) : en activant « Shipping protection », le total
     disparaissait. Cause — le theme re-rend le pied du tiroir a CHAQUE mutation
     du panier (toggle d'assurance, changement de quantite, suppression d'une
     ligne), ce qui detruit `.smob-cs`. Or on ne le reconstruisait que depuis
     swapDrawer/refreshDrawer, c'est-a-dire uniquement apres un ajout au panier.

     Le commentaire d'origine ecartait un MutationObserver au motif qu'il
     tournerait aussi sur les pages sommeil. Ce n'est pas vrai ici : ce fichier
     n'est charge que par les sections `somnair-ob-*`, qui n'existent que dans ce
     template. On garde quand meme la garde `.smob-scope`.

     Anti-boucle : `skinning` neutralise l'observateur pendant nos propres
     ecritures, et on ne reconstruit que si le recapitulatif est REELLEMENT
     absent — apres reconstruction la condition est fausse, donc pas de rappel. */
  function watchDrawer() {
    if (!document.querySelector('.smob-scope')) return;
    var cd = drawer();
    if (!cd || typeof MutationObserver === 'undefined') return;
    var mo = new MutationObserver(function () {
      if (skinning) return;
      var footer = cd.querySelector('.drawer__footer, .cart-drawer__footer');
      if (!footer) return;
      if (footer.querySelector('.smob-cs')) return;
      if (!cd.querySelector('.cart-item')) return;   // panier vide : rien a resumer
      skinDrawer();
    });
    mo.observe(cd, { childList: true, subtree: true });
  }

  /* Formatage minimal, a partir du format que Shopify renvoie dans /cart.js.
     On ne devine ni le symbole ni la position : ils viennent de la boutique. */
  function money(cents, cart) {
    var fmt = (cart && cart.currency) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US',
        { style: 'currency', currency: fmt }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  /* Le contenu remplace le squelette : on le fait APPARAITRE (fondu court +
     legere montee du corps, cf. somnair-cart.css section 8) au lieu de le
     substituer d'un coup. La classe est retiree apres l'animation, sinon les
     repaints SUIVANTS — quantite, pre-cochage de la protection, cross-sell —
     rejoueraient le fondu a chaque fois et le tiroir clignoterait. */
  var enterTimer = null;

  function enterContent(cd) {
    cd.classList.remove('smc-in');
    void cd.offsetWidth;           /* force un reflow : relance l'animation */
    cd.classList.add('smc-in');
    clearTimeout(enterTimer);
    enterTimer = setTimeout(function () { cd.classList.remove('smc-in'); }, 420);
  }

  function swapDrawer(html) {
    var cd = drawer();
    if (!cd) return;
    if (html) {
      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('cart-drawer');
        if (fresh) cd.innerHTML = fresh.innerHTML;
      } catch (e) { /* on garde l'ancien contenu plutot que de vider le tiroir */ }
    }
    var wasLoading = cd.classList.contains('smob-loading');
    /* `is-empty` ne se retire pas les yeux fermes : openDrawer l'enleve AVANT la
       reponse pour que la coquille ne soit pas clippee, mais si l'ajout a echoue
       le panier est reellement vide et le markup frais le dit (.drawer__inner-empty).
       Le retirer quand meme donnerait la page blanche + « Checkout $0.00 » grise
       qu'on vient justement de supprimer. */
    cd.classList.toggle('is-empty', !!cd.querySelector('.drawer__inner-empty'));

    if (!wasLoading) {                 /* repaint ordinaire : rien a devoiler */
      skinDrawer();
      return;
    }

    /* Premiere ouverture : on garde le squelette le temps que buildSummary ait
       pose le pied complet, puis on devoile TOUT d'un coup. Sans cette attente
       le tiroir apparaissait, puis grandissait 150 ms plus tard. Plafond a
       400 ms pour ne jamais rester bloque sur un /cart.js qui traine. */
    var revealed = false;
    var reveal = function () {
      if (revealed) return;
      revealed = true;
      cd.classList.remove('smob-loading');
      enterContent(cd);
    };
    /* 19/10 : on attend aussi le DECODAGE de la vignette du 1er article, sous le
       meme plafond de 400 ms. Mesure : elle arrivait ~490 ms APRES le devoilement
       (722 -> 1 412 ms, CPU x4), donc surgissait dans un tiroir deja ouvert. */
    var im0 = cd.querySelector('#CartDrawer .cart-item__image');
    var imgReady = (im0 && im0.decode) ? im0.decode().catch(function () {}) : Promise.resolve();
    setTimeout(reveal, 400);
    Promise.all([skinDrawer(), imgReady]).then(reveal, reveal);
  }

  function refreshDrawer() {
    return fetch('/?sections=cart-drawer', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (j) { swapDrawer(j && j['cart-drawer']); })
      /* Meme sortie que le succes, contenu inchange : swapDrawer(null) retire le
         squelette ET remet `is-empty` d'apres le DOM en place, au lieu de laisser
         la coquille decollapsee (le panneau blanc a « Checkout $0.00 »). */
      .catch(function () { swapDrawer(null); });
  }

  /* ------------------------------------------------------- cadeau du palier

     Le palier selectionne peut porter un PRODUIT CADEAU (`data-smob-gift`, pose
     par somnair-ob-buy.liquid quand le reglage `gift_handle` resout). Il part
     alors dans le MEME /cart/add que la gamelle, a 0,00 $, pour que la commande
     — donc le bon de commande du fournisseur — porte la ligne. Sans ca le
     cadeau n'existe que sur la page, et le colis part sans lui.

     Un seul appel, pas deux : l'ajout multi-lignes est atomique cote Shopify.
     Deux appels enchaines laisseraient, en cas d'echec du second, une gamelle
     au panier et un cadeau promis manquant — sans que rien ne le signale. */
  function tierGift(form) {
    var t = form.querySelector('[data-smob-tier][aria-checked="true"]')
         || form.querySelector('[data-smob-tier][data-default]');
    var id = t && t.getAttribute('data-smob-gift');
    if (!id) return null;
    /* La quantite est un NOMBRE D'OBJETS, pas un nombre de packs : « 2 FREE
       Feeding Mats » part en 2 unites du vrai tapis. Un SKU « pack de 2 »
       ajoute a 1 laissait le fournisseur deviner si « x2 » voulait dire 2 ou 4
       (sa question du 24/08). */
    var qty = Number(t.getAttribute('data-smob-gift-qty')) || 1;
    return { id: Number(id), qty: qty, label: t.getAttribute('data-smob-gift-label') || '' };
  }

  function initForm(form) {
    var btn = form.querySelector('[data-smob-atc]');

    function addFormData() {
      var fd = new FormData(form);
      fd.append('sections', 'cart-drawer');
      return fetch('/cart/add.js', { method: 'POST', body: fd });
    }

    function addWithGift(gift) {
      var vid = form.querySelector('[data-smob-vid]');
      if (!vid || !vid.value) return addFormData();
      /* ORDRE INVERSE, ce n'est pas une coquetterie : le tiroir (et le
         checkout) listent les lignes de la plus RECENTE a la plus ancienne.
         Envoyer le cadeau en premier le fait donc afficher SOUS la gamelle,
         qui reste en tete — c'est elle qu'on achete. Envoye dans l'ordre
         naturel, le tapis a 0,00 $ s'affichait au-dessus du produit paye. */
      var body = {
        items: [
          /* Proprietes prefixees par « _ » : masquees au client (panier,
             checkout, confirmation) mais lisibles dans l'admin, donc au moment
             de preparer la commande.
               _gift_with  : le libelle du palier, pour un humain ;
               _gift_for   : la variante achetee qui ouvre droit au cadeau ;
               _gift_units : combien d'unites par bundle.
             Les deux dernieres ne sont pas du confort : c'est ce qui permet a
             normaliseGifts() de RECOMPTER la ligne au lieu de lui faire
             confiance. Sans elles, un double clic sur « Add to cart » suivi
             d'une correction de la ligne gamelle laissait 2 cadeaux pour un
             seul bundle (commandes #1139 et #1152). */
          { id: gift.id, quantity: gift.qty, properties: {
              _gift_with: gift.label,
              _gift_for: String(vid.value),
              _gift_units: String(gift.qty)
            } },
          { id: Number(vid.value), quantity: 1 }
        ],
        sections: 'cart-drawer'
      };
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        /* 422 sur le lot = RIEN n'a ete ajoute (atomique). On refait l'ajout
           sans le cadeau : mieux vaut une vente sans tapis qu'un bouton mort. */
        if (!r.ok) return addFormData();
        return r;
      });
    }

    form.addEventListener('submit', function (e) {
      if (e.defaultPrevented) { openDrawer(true); setTimeout(refreshDrawer, 800); return; }
      e.preventDefault();
      /* On ouvre le tiroir AVANT l'aller-retour reseau : c'est ce qui donne la
         sensation d'instantaneite du concurrent. Le spinner tient la place. */
      openDrawer(true);
      if (btn) btn.disabled = true;
      var gift = tierGift(form);
      (gift ? addWithGift(gift) : addFormData())
        .then(function (r) { return r.json(); })
        .then(function (res) {
          var html = res && res.sections && res.sections['cart-drawer'];
          if (html) swapDrawer(html); else refreshDrawer();
        })
        /* L'ajout a echoue (429 anti-bot, rupture...) : on relit le panier reel
           plutot que de decouvrir un tiroir vide sous le squelette. */
        .catch(function () { refreshDrawer(); })
        .then(function () { if (btn) btn.disabled = false; });
    });
  }

  function initCartButtons() {
    all('[data-smob-cart]').forEach(function (b) {
      b.addEventListener('click', function () {
        var cd = openDrawer(false);
        if (!cd) { window.location.href = '/cart'; return; }
        /* Filet : le pied a pu etre repeint — donc .smob-cs detruit — pendant que
           `skinning` neutralisait l'observateur. buildSummary retire l'ancien
           recapitulatif avant de le reposer, le rappel ne cree donc pas de doublon. */
        /* 19/10 : APRES le glissement (300 ms), jamais pendant. Mesure : a la
           reouverture, /cart.js puis remplacement du pied a t+124 ms, en plein
           glissement, 2 frames perdues. Dans le cas normal le recapitulatif est
           deja la (boot l'a pose) : le filet ne joue que s'il manque. */
        if (cd.querySelector('.cart-item') && !cd.querySelector('#CartDrawer .smob-cs')) {
          setTimeout(skinDrawer, 320);
        }
      });
    });
  }

  /* ------------------------------------------------------- menu mobile */

  function initBurger(root) {
    var btn = root.querySelector('[data-smob-burger]');
    var menu = root.querySelector('[data-smob-mobmenu]');
    if (!btn || !menu) return;
    btn.addEventListener('click', function () {
      var open = menu.classList.toggle('open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------ apparition au scroll
     Leur page anime 28 noeuds (Dawn animations.js). La notre peignait tout d'un
     coup. Meme geste : on revele une fois, on n'observe plus ensuite.
     `--smob-anim-order` decale les enfants d'un meme groupe, comme leur
     `--animation-order`. */

  function initReveal() {
    /* `data-smob-reveal` seulement : `data-smob-anim` seule sert d'echappatoire au
       force-visible (mini-avis, bandeau haut) et ne doit PAS etre masquee ici. */
    var nodes = all('[data-smob-reveal]');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window) ||
        matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }
    /* On arme l'etat masque SEULEMENT maintenant : tant que cette classe n'est pas
       posee, le CSS laisse tout visible. Si ce script n'avait pas tourne, la page
       resterait donc lisible au lieu de rester blanche. */
    all('.smob-scope').forEach(function (s) { s.classList.add('smob-reveal-on'); });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.01 });
    nodes.forEach(function (n) { io.observe(n); });

    /* FILET DE SECURITE. Une animation ratee doit degrader vers « visible », jamais
       vers « invisible ». Mesure du 03/08 : apres un defilement complet, une partie
       des noeuds n'avait toujours pas recu `is-in` — l'observer ne se declenche pas
       si l'element traverse le viewport plus vite que son callback, et le
       `rootMargin` negatif ignore la derniere bande de l'ecran. Passe ce delai, on
       revele tout ce qui reste : au pire l'entree se fait sans animation. */
    setTimeout(function () {
      nodes.forEach(function (n) {
        if (!n.classList.contains('is-in')) {
          n.classList.add('is-in');
          io.unobserve(n);
        }
      });
    }, 4000);
  }

  /* ------------------------------------------- barre ATC collante (mobile)
     `stickyAtcEnabled: true` chez eux : « Orthopedic Feeder » + « Choose bundle »,
     390x91, fond #F5F9FF. Sans elle, plus aucun bouton d'achat sur ~3 700 px. */

  function initSticky(bar) {
    var anchor = document.querySelector('[data-smob-atc]');
    if (!anchor || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      /* Visible des que l'ATC principal est sorti par le HAUT du viewport. */
      var e = entries[0];
      var gone = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.classList.toggle('is-on', gone);
    }, { threshold: 0 });
    io.observe(anchor);

    var btn = bar.querySelector('[data-smob-sticky-cta]');
    if (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var tiers = document.querySelector('[data-smob-tiers]') || anchor;
        tiers.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  /* ------------------------------------------------- ticket a gratter
     Frere du pop-up de la marque sommeil (snippets/somnair-popup.liquid), que
     `somnair-ob-scope` retire du DOM sur cette page : celui-ci ne parle que de
     la gamelle et aucun de ses boutons ne quitte la page.

     TROIS REGLES A NE PAS PERDRE DE VUE :
     · le voile et l'invite s'effacent en OPACITE -> ils portent `data-smob-anim`
       cote Liquid, sans quoi le force-visible du scope les rend indelebiles ;
     · tout le reste se masque en `display` (attribut `hidden`), jamais en
       opacite ni en visibilite, forcees par le scope ;
     · aucun montant n'est calcule ici (regle du fichier) — on n'affiche qu'un
       pourcentage de reduction, jamais un prix. */

  function initScratch(root) {
    var key = root.getAttribute('data-key') || 'smobSc_v1';
    var design = !!(window.Shopify && window.Shopify.designMode);
    /* Dans l'editeur de theme on reaffiche toujours, sinon le marchand ne peut
       plus voir sa propre section apres un premier apercu. */
    if (!design) {
      try { if (localStorage.getItem(key + ':seen')) return; } catch (e) {}
    }
    /* Ne jamais recouvrir le panier ni le checkout. */
    if (/\/(cart|checkout)/.test(location.pathname)) return;

    var card = root.querySelector('[data-smob-sc-card]');
    var ticket = root.querySelector('[data-smob-sc-ticket]');
    var canvas = root.querySelector('[data-smob-sc-foil]');
    var hint = root.querySelector('[data-smob-sc-hint]');
    var done = root.querySelector('[data-smob-sc-done]');
    var copyBtn = root.querySelector('[data-smob-sc-copy]');
    var ctaBtn = root.querySelector('[data-smob-sc-cta]');
    var a11yBtn = root.querySelector('[data-smob-sc-a11y]');
    if (!card || !ticket || !canvas) return;
    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var THRESHOLD = 0.55, IDLE_MS = 10000;
    /* `dismissed` : une fois le ticket ferme, il ne se rouvre PLUS de la visite.
       Sans lui, deux chemins le faisaient revenir tout seul (signale le 04/08) —
       le minuteur d'ouverture qui arrive apres une fermeture provoquee par la
       sortie de page, et la sortie de page elle-meme, qui restait a l'ecoute
       apres la fermeture. Une modale qu'on ferme et qui revient est un bug, pas
       une relance. */
    var dismissed = false;
    var revealed = false, drawing = false, last = null, lastCheck = 0;
    var idleTimer = null, rzTimer = null, lastW = 0, lastH = 0;
    var lastFocus = null, prevOverflow = '', prevPadRight = '';
    var copyLabel = copyBtn ? copyBtn.textContent : '';

    /* --- lot pondere : `data-odds` est la chance du lot A en pourcentage, le
       lot B recoit le reste. Un seul nombre, donc deux poids qui ne peuvent pas
       diverger. Le tirage est memorise : rafraichir la page ne redonne pas
       une seconde chance. */
    var prizes = [
      { pct: root.getAttribute('data-pct-a'), code: root.getAttribute('data-code-a') },
      { pct: root.getAttribute('data-pct-b'), code: root.getAttribute('data-code-b') }
    ];
    function pickPrize() {
      var odds = parseFloat(root.getAttribute('data-odds'));
      if (isNaN(odds)) odds = 90;
      return (Math.random() * 100 < odds || !prizes[1].code) ? prizes[0] : prizes[1];
    }
    function resolvePrize() {
      try {
        var saved = localStorage.getItem(key + ':prize');
        for (var i = 0; i < prizes.length; i++) if (prizes[i].code === saved) return prizes[i];
      } catch (e) {}
      var pick = pickPrize();
      try { localStorage.setItem(key + ':prize', pick.code); } catch (e) {}
      return pick;
    }
    var prize = resolvePrize();

    root.querySelector('[data-smob-sc-pct]').textContent = '−' + prize.pct + '%';
    root.querySelector('[data-smob-sc-code]').textContent = prize.code;
    canvas.setAttribute('aria-label', 'Scratch ticket hiding a ' + prize.pct + '% discount code');

    /* --- couche argentee a gratter --- */
    function paintFoil() {
      var w = ticket.clientWidth, h = ticket.clientHeight;
      if (!w || !h) return;
      lastW = w; lastH = h;
      var dpr = Math.min(window.devicePixelRatio || 1, 3);
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      canvas.style.width = w + 'px'; canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, w, h);
      var g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, '#b9c4da'); g.addColorStop(.35, '#eef2f8');
      g.addColorStop(.62, '#c9d2e4'); g.addColorStop(1, '#aab7d0');
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      /* Le foil est CLAIR : du blanc a .6 y serait illisible (mesure du 31/07 sur
         le pop-up sommeil). On grave en navy, avec un relief blanc dessous. */
      ctx.font = "700 " + Math.round(Math.min(h * .18, 22)) + "px 'Aleo',Georgia,serif";
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      try { ctx.letterSpacing = '2px'; } catch (e) {}   /* ignore la ou il n'existe pas */
      ctx.fillStyle = 'rgba(255,255,255,.8)';
      ctx.fillText('SCRATCH HERE', w / 2, h / 2 + 1.5);
      ctx.fillStyle = '#22315c';
      ctx.fillText('SCRATCH HERE', w / 2, h / 2);
      try { ctx.letterSpacing = '0px'; } catch (e) {}
      ctx.globalCompositeOperation = 'destination-out';
    }

    function pointAt(e) {
      var r = canvas.getBoundingClientRect();
      var t = (e.touches && e.touches[0]) || e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    }
    function scratchTo(p) {
      ctx.lineWidth = 38; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      if (last) { ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(p.x, p.y); ctx.stroke(); }
      ctx.beginPath(); ctx.arc(p.x, p.y, 19, 0, Math.PI * 2); ctx.fill();
      last = p;
    }
    function clearedRatio() {
      var w = canvas.width, h = canvas.height, data;
      if (!w || !h) return 0;
      try { data = ctx.getImageData(0, 0, w, h).data; } catch (e) { return 0; }
      var step = 8, total = 0, clear = 0, x, y;
      for (y = 0; y < h; y += step) {
        for (x = 0; x < w; x += step) {
          total++;
          if (data[(y * w + x) * 4 + 3] === 0) clear++;
        }
      }
      return total ? clear / total : 0;
    }
    function maybeReveal(force) {
      var now = new Date().getTime();
      if (!force && now - lastCheck < 300) return;
      lastCheck = now;
      if (clearedRatio() >= THRESHOLD) reveal();
    }

    /* Pose du code dans la session. DESACTIVE par defaut depuis le 04/08 : le
       client copie son code et le saisit lui-meme au checkout — c'est ce geste
       qui fait exister le gain a ses yeux. Le reglage reste la si on veut un
       jour revenir a l'application automatique. */
    function applyCode() {
      if (root.getAttribute('data-apply') !== '1' || !prize.code) return;
      try {
        fetch('/discount/' + encodeURIComponent(prize.code) + '?redirect=/cart.js',
              { credentials: 'same-origin' }).catch(function () {});
      } catch (e) {}
    }

    function armIdle() { clearTimeout(idleTimer); idleTimer = setTimeout(reveal, IDLE_MS); }
    function reveal() {
      if (revealed) return;
      revealed = true;
      clearTimeout(idleTimer);
      canvas.classList.add('is-gone');
      setTimeout(function () { canvas.hidden = true; }, 350);
      if (hint) hint.hidden = true;
      if (a11yBtn) a11yBtn.hidden = true;
      if (done) done.hidden = false;
      applyCode();
      try { localStorage.setItem(key + ':seen', '1'); } catch (e) {}
    }

    function start(e) {
      if (revealed) return;
      drawing = true; last = null;
      clearTimeout(idleTimer);
      if (hint) hint.classList.add('is-out');
      if (canvas.setPointerCapture && e.pointerId != null) {
        try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
      }
      scratchTo(pointAt(e));
      e.preventDefault();
    }
    function move(e) {
      if (!drawing || revealed) return;
      scratchTo(pointAt(e));
      maybeReveal(false);
      e.preventDefault();
    }
    function end() {
      if (!drawing) return;
      drawing = false; last = null;
      maybeReveal(true);
      if (!revealed) armIdle();
    }

    if (window.PointerEvent) {
      canvas.addEventListener('pointerdown', start);
      canvas.addEventListener('pointermove', move);
      canvas.addEventListener('pointerup', end);
      canvas.addEventListener('pointercancel', end);
    } else {
      canvas.addEventListener('mousedown', start);
      canvas.addEventListener('mousemove', move);
      document.addEventListener('mouseup', end);
      canvas.addEventListener('touchstart', start, { passive: false });
      canvas.addEventListener('touchmove', move, { passive: false });
      canvas.addEventListener('touchend', end);
    }
    if (a11yBtn) a11yBtn.addEventListener('click', reveal);

    /* Copie du code. `flash` n'est vrai que pour le bouton dedie : le CTA copie
       en silence, il enchaine deja sur la fermeture. */
    function copyCode(flash) {
      function ok() {
        if (!flash || !copyBtn) return;
        copyBtn.textContent = 'Copied ✓';
        copyBtn.classList.add('is-copied');
        setTimeout(function () {
          copyBtn.textContent = copyLabel;
          copyBtn.classList.remove('is-copied');
        }, 2000);
      }
      function fallback() {
        try {
          var ta = document.createElement('textarea');
          ta.value = prize.code; ta.setAttribute('readonly', '');
          ta.style.cssText = 'position:absolute;left:-9999px;top:0';
          document.body.appendChild(ta); ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          ok();
        } catch (e) {}
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(prize.code).then(ok, fallback);
      } else fallback();
    }
    if (copyBtn) copyBtn.addEventListener('click', function () { copyCode(true); });

    /* Le CTA ne SORT pas de la page (etancheite) : il ferme et amene aux paliers.
       Il COPIE AUSSI le code au passage. Depuis que le code se saisit a la main
       au checkout (04/08), fermer sans avoir copie ferait perdre le lot : le
       ticket ne se rouvre plus, et le geste principal de la carte est justement
       de repartir avec le code. */
    if (ctaBtn) {
      ctaBtn.addEventListener('click', function () {
        copyCode(false);
        close();
        var t = document.querySelector('[data-smob-tiers]') || document.querySelector('[data-smob-atc]');
        if (t) t.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    /* --- verrou de scroll : en plein ecran la page derriere ne defile plus --- */
    function lockScroll() {
      var sb = window.innerWidth - document.documentElement.clientWidth;
      prevOverflow = document.body.style.overflow;
      prevPadRight = document.body.style.paddingRight;
      document.body.style.overflow = 'hidden';
      if (sb > 0) document.body.style.paddingRight = sb + 'px';  /* evite le saut de mise en page */
    }
    function unlockScroll() {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPadRight;
    }

    /* --- piege de focus : la page derriere est masquee, le clavier n'en sort pas --- */
    function focusables() {
      var sel = 'button,a[href],input,select,textarea,[tabindex]:not([tabindex="-1"])';
      return Array.prototype.filter.call(root.querySelectorAll(sel), function (el) {
        return !el.hasAttribute('hidden') && el.getClientRects().length > 0 && !el.disabled;
      });
    }

    function close() {
      dismissed = true;
      if (root.hasAttribute('hidden')) return;
      root.setAttribute('hidden', '');
      clearTimeout(idleTimer);
      unlockScroll();
      if (!design) { try { localStorage.setItem(key + ':seen', '1'); } catch (e) {} }
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    }
    function open() {
      if (dismissed) return;
      if (!root.hasAttribute('hidden')) return;
      lastFocus = document.activeElement;
      root.removeAttribute('hidden');
      lockScroll();
      paintFoil();
      armIdle();
      try { card.focus({ preventScroll: true }); } catch (e) { try { card.focus(); } catch (e2) {} }
    }
    all('[data-smob-sc-close]', root).forEach(function (el) { el.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) {
      if (root.hasAttribute('hidden')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0], lastEl = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === card)) {
        e.preventDefault(); lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault(); first.focus();
      }
    });
    window.addEventListener('resize', function () {
      if (revealed || root.hasAttribute('hidden')) return;
      clearTimeout(rzTimer);
      /* Ne repeindre — donc perdre le grattage en cours — que si le ticket a
         VRAIMENT change de taille : sur mobile, la barre d'adresse qui se
         retracte declenche un resize a chaque defilement. */
      rzTimer = setTimeout(function () {
        if (ticket.clientWidth !== lastW || ticket.clientHeight !== lastH) paintFoil();
      }, 200);
    });

    var delay = parseInt(root.getAttribute('data-delay'), 10);
    if (isNaN(delay)) delay = 5000;
    setTimeout(open, delay);

    /* Sortie de page : ordinateur seulement (un doigt ne « quitte » pas par le
       haut). Ouvre le ticket avant la fin du delai plutot que de le perdre. */
    if (root.getAttribute('data-exit') === '1' && matchMedia('(hover: hover)').matches) {
      document.addEventListener('mouseout', function (e) {
        if (e.clientY <= 0 && !e.relatedTarget) open();
      });
    }
  }

  /* ------------------------------------------------------------- boot */

  function boot() {
    all('[data-smob-gallery]').forEach(initGallery);
    all('[data-smob-countdown]').forEach(initCountdown);
    all('[data-smob-ship]').forEach(initShipDate);
    all('[data-smob-quotebox]').forEach(initQuotes);
    /* L'ordre compte : le selecteur de taille pose la variante, les paliers
       posent la quantite. `initTiers` en dernier pour que sa selection par
       defaut ait le dernier mot sur la quantite. */
    all('[data-smob-sizes]').forEach(initSizes);
    all('[data-smob-modes]').forEach(initModes);
    all('[data-smob-tiers]').forEach(initTiers);
    all('[data-smob-stock]').forEach(initStock);
    all('[data-smob-carousel]').forEach(initCarousel);
    all('[data-smob-stories]').forEach(initStories);
    all('[data-smob-ticker]').forEach(initTicker);
    all('[data-smob-form]').forEach(initForm);
    all('[data-smob-header]').forEach(initBurger);
    all('[data-smob-sticky]').forEach(initSticky);
    all('[data-smob-scratch]').forEach(initScratch);
    initCartButtons();
    initReveal();
    /* La barre d'objectif est rendue par le theme AVANT tout ajout au panier :
       sans cet appel au demarrage, elle affichait encore le seuil de 80 $ du
       theme jusqu'au premier ajout. skinDrawer ne suffit pas, il ne tourne
       qu'apres une mutation du panier. */
    var cd0 = drawer();
    if (cd0) goalBar(cd0);
    /* MEME PIEGE, pour le recapitulatif du pied (« Discounts » / « Subtotal » et
       les icones de paiement) : buildSummary ne tourne que depuis swapDrawer /
       refreshDrawer — donc apres un ajout au panier SUR CETTE PAGE — ou depuis
       watchDrawer, qui n'observe que childList/subtree du tiroir. Or ouvrir le
       tiroir depuis l'icone panier ne fait que poser des classes sur
       <cart-drawer> (openDrawer) : aucune mutation observee, donc aucun appel.
       Resultat constate le 13/08 : en revenant sur la page avec un panier deja
       rempli, le pied n'affichait NI les deux rangees NI les icones — il fallait
       toucher une quantite pour les faire apparaitre. On reconstruit donc au
       demarrage des que le tiroir porte deja une ligne. */
    if (cd0 && cd0.querySelector('.cart-item')) skinDrawer();
    watchDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
